import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
    SHUI_MODAL_CONFIG,
    ShButtonComponent,
    ShInputTextComponent,
    ShMenuItemComponent,
    ShSpinnerComponent,
    ShuiModalConfig,
    ShuiModalRef,
    TextValueAccessor
} from '@gbr-monorepo/gbr-shui';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, finalize, Observable, startWith, switchMap } from 'rxjs';
import { Store } from '@ngxs/store';
import { WorkersCouncilGroupState } from '../../state/workers-council-group.state';
import { User } from '@gbr-monorepo/interfaces';
import { LoadDelegateUsers } from '../../state/workers-council-group.action';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-select-delegate-dialog',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ShButtonComponent,
        ShInputTextComponent,
        ShMenuItemComponent,
        ShSpinnerComponent,
        TextValueAccessor,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './select-delegate-dialog.component.html'
})
export class SelectDelegateDialogComponent implements OnInit {
    private store = inject(Store);
    private readonly destroyRef = inject(DestroyRef);
    dialogRef = inject<ShuiModalRef<User>>(ShuiModalRef);
    config = inject<ShuiModalConfig<{ onAdd: (user: User) => void }>>(SHUI_MODAL_CONFIG);

    protected readonly workersCouncilUsers$ = inject(Store).select(
        WorkersCouncilGroupState.getWorkersCouncilMembersOptions
    );

    protected isLoading = false;
    protected readonly searchControl = new FormControl('', { nonNullable: true });
    protected readonly selectControl = new FormControl<User | null>(null);

    public compareFn = (firstUser: User, secondUser: User) =>
        firstUser && secondUser && firstUser.gid === secondUser.gid;

    public ngOnInit(): void {
        this.searchControl.valueChanges
            .pipe(
                startWith(this.searchControl.value),
                debounceTime(300),
                distinctUntilChanged(),
                switchMap(search => this.searchFn(search, 1)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    public searchFn(search: string, page: number): Observable<void> {
        this.isLoading = true;
        return this.store
            .dispatch(new LoadDelegateUsers(search, Math.max(0, page - 1)))
            .pipe(finalize(() => (this.isLoading = false)));
    }

    protected selectUser(user: User): void {
        this.selectControl.setValue(user);
    }

    public addSelectedUser(): void {
        if (this.selectControl.value) {
            this.config.data?.onAdd(this.selectControl.value);
        }
        this.dialogRef.close();
    }
}
